import { useSearchParams } from 'react-router-dom';
import { ViewToggle } from '@/components/ui/ViewToggle';
import { EVENT_CAPACITY_QUERY_KEYS } from '@/components/client-deliverables/EventCapacityPanel';
import { useAuth } from '@/lib/use-auth';
import { NAV_SURFACE_MODULE } from '@/lib/permissions';
import { ClientDeliverablesPage } from '@/pages/ClientDeliverablesPage';
import { FutureBillablesTrackerPage } from '@/pages/FutureBillablesTrackerPage';
import { CompaniesPage } from '@/pages/CompaniesPage';

/**
 * The Operations dashboard — three screens that already exist, behind one strip
 * of pills.
 *
 * Owner's instruction: *"no need to reinvent the wheel, just what's already
 * built."* So this file holds no table, no filter bank and no data fetching of
 * its own. It mounts `ClientDeliverablesPage`, `FutureBillablesTrackerPage` or
 * `CompaniesPage` unchanged, and everything below is about the two things a
 * container like this can get wrong: WHICH ONE is mounted, and what is left
 * behind in the query string when the reader switches.
 *
 * ── ONE PILL IS MOUNTED AT A TIME ─────────────────────────────────────────
 *
 * 🔴 **They are NOT all mounted with two hidden**, and that is a decision taken
 * against measurement rather than a matter of taste. Three of these pages
 * mounted together fire roughly twenty-one to twenty-three requests on arrival,
 * against a limit shared by the whole office. ⚠️ *SUPERSEDED: "a **120-per-60s
 * limit shared by the whole office** — so one person opening this page spends a
 * fifth of everybody's budget."* It is **12,000/60s** (corrected 2026-09-17), so
 * that fifth is nearer two thousandths and the arithmetic now argues for nothing.
 * **The waste is the point, not the headroom** — every one of those requests
 * renders two screens nobody is looking at. It also puts three `<h1>`s inside one
 * `<main>`, and the half below is the one that actually bites.
 *
 * ⚠️ **And the worst of it is silent.** Each of these pages runs its own
 * `useSavedFilterApply`, which builds a WHOLE query string from the render's
 * snapshot and calls `setSearchParams`. Three of them mounted at once is three
 * writers racing off one stale snapshot: last write wins, the other two
 * seedings vanish, and **nothing errors** — the reader simply gets a screen
 * whose filters are somebody else's. The same reasoning the Accounts Dashboard
 * records for its own sections, one level up.
 *
 * ── AND THIS PAGE RENDERS NO `PageHeader` ─────────────────────────────────
 *
 * ⚠️ **That looks like an omission and is the opposite of one.** All three
 * children render their own `PageHeader` — with an `<h1>`, an eyebrow and real
 * actions (the tracker's view switch, Companies' New company). A header here
 * would put a second `<h1>` in the same `<main>`, and the reader would be told
 * the page is called one thing by a heading that never changes while the screen
 * beneath it does. Leaving it to the child is what makes the heading FOLLOW the
 * pill, which is the honest arrangement: the title, the actions and the content
 * are one screen's, chosen together.
 *
 * So the pills sit in a plain container above the child, and nothing else here
 * draws chrome.
 */

/* -------------------------------------------------------------------------
 * The pills
 * --------------------------------------------------------------------- */

type Tab = 'deliverables' | 'billables' | 'companies';

const TABS: readonly { value: Tab; label: string }[] = [
  { value: 'deliverables', label: 'Client Deliverables' },
  { value: 'billables', label: 'Future Billables' },
  { value: 'companies', label: 'Companies' },
];

/**
 * ⚠️ **`role="group"` with `aria-pressed`, NOT `role="tab"`.** These switch what
 * the page below IS, rather than labelling panels that share one tabstop —
 * matching every other page-level view switch in this app (`/companies`,
 * `/quotes`, `/invoices`, `/deals`, `/client-deliverables`, the Future Billables
 * Tracker, the Accounts and Management dashboards). A real tablist would promise
 * arrow-key navigation between panels that do not share a tabstop.
 *
 * ⚠️ **Copied from `ManagementDashboardPage` and widened from two members to
 * three, deliberately NOT extracted into a shared primitive.** Six hand-rolled
 * copies of this shape exist; unifying them touches files other sessions are
 * editing, and a half-done extraction leaves the app with seven. It is also not
 * `SegmentedControl`, whose own header says it is shaped for input modes inside
 * a grid cell.
 *
 * ⚠️ **Every pill renders at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a toggle. The selected pill is not distinguished by colour
 * alone: `secondary` gives it a raised surface against `ghost`'s transparent
 * one, and `aria-pressed` states it outright.
 */
function TabToggle({
  value,
  tabs,
  onChange,
}: {
  value: Tab;
  tabs: readonly { value: Tab; label: string }[];
  onChange: (next: Tab) => void;
}) {
  return <ViewToggle label="Operations view" value={value} options={[...tabs]} onChange={onChange} />;
}

/* -------------------------------------------------------------------------
 * What a switch clears
 * --------------------------------------------------------------------- */

const TAB_PARAM = 'tab';

/**
 * 🔴 **EVERY QUERY KEY ANY PILL OWNS, INCLUDING BOTH VIEW KEYS.**
 *
 * All three screens live in ONE query string and their key names OVERLAP —
 * `companyId` is a filter on two of them — so a key carried across a switch
 * applies one screen's narrowing to another's list, silently. A short list is
 * not a smaller version of the right one: it is a filtered table under an
 * unfiltered heading, which is this codebase's most repeated failure.
 *
 * Measured against the three pages' own `FILTER_KEYS` / `readFilters` on
 * 2026-09-09, rather than copied from a brief:
 *
 * - `ClientDeliverablesPage`  `ID_FILTER_KEYS` + `ENUM_FILTER_KEYS` (:190-201)
 * - `FutureBillablesTrackerPage`  `ID_` + `TEXT_` + `FLAG_FILTER_KEYS` (:302-315)
 * - `CompaniesPage`  `SAVED_FILTER_KEYS` (:127) + `SAVED_VIEW_KEYS` (:133)
 *
 * 🔴 **`view` IS THE DANGEROUS ONE AND MUST NEVER BE LEFT BEHIND.** The two
 * pages that read it read it in OPPOSITE directions:
 *
 * - `ClientDeliverablesPage:2026` — `searchParams.get('view') === 'table'`, so
 *   **anything else, `board` included, means the Google Sheet mirror**;
 * - `CompaniesPage:1651` — `searchParams.get('view') === 'board'`, so anything
 *   else means the table.
 *
 * So a reader who opens the Companies board and switches to Deliverables would
 * carry `?view=board` across and land in sheet mode — a different view of a
 * different page, reached by touching neither, with a `200` and nothing failing.
 *
 * ⚠️ **`display` is a THIRD view key and is not a spelling of `view`.**
 * `FutureBillablesTrackerPage:180` uses `?display=` precisely BECAUSE that
 * tracker family already means a population by `view` — see its own header. It
 * is cleared here for the same reason as the other two: it is a key a pill owns.
 *
 * ⚠️ **Add to this list whenever any of the three pages gains a query key.**
 * Nothing here can notice that on its own — the failure is a stale filter, not
 * an error — so this is the list to re-derive, in those files, before trusting.
 */
const CLEARED_ON_SWITCH = [
  /*
   * 🔴 **THE PROJECT CAPACITY TAB'S THREE KEYS, IMPORTED RATHER THAN RETYPED.**
   * `?deliverables=capacity` is the second tab on the Client Deliverables screen
   * — a DISTINCT key, because this file owns `?tab=` — plus its own cycle and
   * project filters. They are spread from the component that defines them so the
   * two lists cannot drift: the warning immediately above says nothing here can
   * notice a page gaining a key, and an import is the only version of that which
   * cannot be forgotten.
   *
   * ⚠️ **`deliverables` itself is cleared, deliberately.** A reader on the
   * capacity tab who switches to Companies and back lands on the tracker, which
   * is how `view` and `display` already behave — the pill decides the screen, and
   * a sub-tab surviving the pill it belongs to is a leftover, not a preference.
   */
  ...EVENT_CAPACITY_QUERY_KEYS,
  // ClientDeliverablesPage
  'companyId',
  'productId',
  'projectId',
  'intakeId',
  'cycleId',
  'regionId',
  'source',
  'billingStatus',
  'outstanding',
  // FutureBillablesTrackerPage (`companyId` shared with the above)
  'currency',
  'kind',
  'amountMin',
  'amountMax',
  'plannedIn30Days',
  'plan',
  // CompaniesPage
  'stageId',
  // Both view keys — read in opposite directions, see above.
  'view',
  'display',
] as const;

/* -------------------------------------------------------------------------
 * The page
 * --------------------------------------------------------------------- */

export function OperationsDashboardPage() {
  const { ability } = useAuth();

  /*
   * Which pills to OFFER. Each child already gates itself and renders its own
   * refusal panel, so this decides the strip and nothing else.
   *
   * ⚠️ These DEGRADE OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API or
   * a failed `/permissions/me` all leave every pill visible, which is the safe
   * direction: a hidden pill is an optimisation, the server's refusal is the
   * guarantee.
   */
  const canRead: Record<Tab, boolean> = {
    deliverables: ability.canRead(NAV_SURFACE_MODULE.trackerClientDeliverables),
    billables: ability.canRead(NAV_SURFACE_MODULE.trackerInvoice),
    companies: ability.canRead('companies'),
  };

  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT, not options — `useSearchParams({ replace: true })` injects a literal
   * `replace=true` into the query string and then into everything parsed out of
   * it, by this page and by the child below it. The `{ replace: true }` belongs
   * on the setter.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get(TAB_PARAM);
  /*
   * The default is spelled by the key's ABSENCE, so `/operations` and
   * `/operations?tab=deliverables` are one URL for one screen rather than two
   * for it. Anything unrecognised reads as the default too — an unknown pill is
   * not a reason to show nothing.
   */
  const tab: Tab = raw === 'billables' || raw === 'companies' ? raw : 'deliverables';

  /*
   * ⚠️ **NOTHING IS WRITTEN TO THE URL ON ARRIVAL, and that is load-bearing.**
   * The mounted child seeds its own saved filters into this same query string on
   * its first render. An arrival write from here would race that seeding off a
   * snapshot taken before it — one of the two would lose, silently, and the
   * reader would get the right screen with the wrong filters. The default is a
   * value read, never a value written.
   */

  const changeTab = (next: Tab) => {
    if (next === tab) return;
    /*
     * 🔴 **ONE `URLSearchParams`, EVERY WRITE, ONE `setSearchParams`.** Calling
     * this and then a child's setter would look right and would wipe one of
     * them: the second write starts from the render's snapshot of the query
     * string, which the first has not updated yet, so whichever lands last is
     * the only one that survives. Nothing errors.
     */
    const params = new URLSearchParams(searchParams);
    if (next === 'deliverables') params.delete(TAB_PARAM);
    else params.set(TAB_PARAM, next);
    for (const key of CLEARED_ON_SWITCH) params.delete(key);
    /*
     * `{ replace: true }`: switching a pill is adjusting what is on screen, not
     * navigating, so it must not put a Back-button step between the reader and
     * the page they arrived from.
     */
    setSearchParams(params, { replace: true });
  };

  /*
   * ⚠️ **A pill the reader may not open is HIDDEN from the strip and still
   * MOUNTED when the URL names it.** Redirecting a denied `?tab=` to a pill they
   * can see would answer a question nobody asked — the reader followed a link to
   * one screen and silently got another, with nothing on screen saying so. The
   * child renders its own refusal, in its own words, under its own heading.
   */
  const tabs = TABS.filter((entry) => canRead[entry.value] || entry.value === tab);

  return (
    <div className="flex flex-col">
      {/*
        No `PageHeader` here — see this file's header. The child below renders
        the page's only `<h1>`, so the title and the actions change with the
        pill instead of standing over it saying something else.
      */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <TabToggle value={tab} tabs={tabs} onChange={changeTab} />
      </div>

      {/*
        Exactly ONE child, mounted by the pill. Not three with two hidden — see
        the request budget and the racing saved-filter writers in this file's
        header. Each is keyed on nothing and switched by a branch, so the
        outgoing screen unmounts and its state goes with it, which is what makes
        the cleared query string above the whole of what carries across.
      */}
      {tab === 'deliverables' && <ClientDeliverablesPage />}
      {tab === 'billables' && <FutureBillablesTrackerPage />}
      {tab === 'companies' && <CompaniesPage />}
    </div>
  );
}
